import React, { useState, useMemo, useCallback } from "react";
import { Table, Tabs, Tag, Input } from "antd";
import moment from "moment";
import { capitalizeFirstLetter, getStateColor } from "../helpers/utilities";

interface Changelog {
  key?: string;
  createdAt: string;
  newValue?: string;
  auditItem?: string;
  user: string;
  action?: string;
  comment?: string;
}

interface MultipleChangelog {
  audits: Changelog[];
  stateAudits: Changelog[];
}

interface RequestChangeLogsProps {
  data: MultipleChangelog | Changelog[];
  isLoading?: boolean;
}

const RequestChangeLogs: React.FC<RequestChangeLogsProps> = ({ data, isLoading }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const requestChangelogColumns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      width: "25%",
      render: (text: string) => {
        return (
          <span>
            {moment(text).format("llll")} <br />
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "newValue",
      key: "status",
      render(state: string): JSX.Element {
        return (
          <Tag
            color={getStateColor(state)}
            style={{ textTransform: "capitalize" }}
          >
            {state?.replace(/_/g, " ") || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      align: "left" as const,
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      align: "left" as const,
      render: (text: string) => <span>{text}</span>,
    },
  ];

  const changelogColumns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      width: "25%",
      render: (text: string) => {
        return (
          <span>
            {moment(text).format("llll")} <br />
          </span>
        );
      },
    },
    {
      title: "Audit",
      dataIndex: "auditItem",
      key: "audit",
      align: "left" as const,
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      align: "left" as const,
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      align: "left" as const,
      render: (text: string) => <span>{capitalizeFirstLetter(text)}</span>,
    },
    {
      title: "Comments",
      dataIndex: "comment",
      key: "comment",
      align: "left" as const,
      render: (text: string) => <span>{text}</span>,
    },
  ];

  let audits: Changelog[] = [];
  let stateAudits: Changelog[] = [];

  if (Array.isArray(data)) {
    stateAudits = data.map((log: Changelog, index: number) => ({
      ...log,
      key: `audit-${index}`,
    }));
  } else if (data?.audits && data?.stateAudits) {
    audits = data.audits.map((log: Changelog, index: number) => ({
      ...log,
      key: `audit-${index}`,
    }));
    stateAudits = data.stateAudits.map((log: Changelog, index: number) => ({
      ...log,
      key: `stateAudit-${index}`,
    }));
  }

  const filterLogs = useCallback(
    (logs: Changelog[]) => {
      if (!searchTerm) return logs;

      const searchTermLower = searchTerm.toLowerCase();

      return logs.filter(
        (log) =>
          log.auditItem?.toLowerCase().includes(searchTermLower) ||
          log.comment?.toLowerCase().includes(searchTermLower),
      );
    },
    [searchTerm],
  );

  const filteredAudits = useMemo(
    () => filterLogs(audits),
    [audits, filterLogs],
  );
  const filteredStateAudits = useMemo(
    () => filterLogs(stateAudits),
    [stateAudits, filterLogs],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <>
      <div style={{ marginBottom: "20px" }}>
        <Input.Search
          size="middle"
          type="text"
          placeholder="Filter by audit description, date..."
          onChange={handleSearch}
          value={searchTerm}
        />
      </div>
      <Tabs defaultActiveKey="1">
        <Tabs.TabPane tab="State" key="1">
          <Table
            style={{ width: "100%" }}
            bordered={true}
            columns={requestChangelogColumns}
            dataSource={filteredStateAudits}
            pagination={false}
            locale={{ emptyText: "No state logs found" }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Changes" key="2">
          <Table
            style={{ width: "100%" }}
            bordered={true}
            columns={changelogColumns}
            dataSource={filteredAudits}
            pagination={false}
            locale={{ emptyText: "No change logs found" }}
          />
        </Tabs.TabPane>
      </Tabs>
    </>
  );
};

export default RequestChangeLogs;